// src/lib/support.ts — nomenclatoarele tichetelor de suport (F14-T1) + schema contextului paginii.
// PUR (fără DB), partajat server↔client: stările, tipurile, etichetele RO, tranzițiile legale,
// lista ALBĂ a contextului tehnic și regula deterministă „bug din context”.
//
// Contextul e tot ce aplicația strânge singură la escaladare, FĂRĂ date personale prin construcție:
// rută fără query string, slug-uri și id-uri (nu nume), rolul, modulele, versiunea, browserul,
// ultimele erori de client și cereri API eșuate. Lista e închisă (`additionalProperties: false`) și
// plafonată — orice depășire e refuzată, nu trunchiată tăcut.

import { Type, type Static } from "@sinclair/typebox"

export const SUPPORT_STATUSES = ["chat", "escalated", "answered", "closed"] as const
export type SupportStatus = (typeof SUPPORT_STATUSES)[number]
export const SUPPORT_STATUS_LABEL_RO: Record<SupportStatus, string> = {
	chat: "Cu asistentul",
	escalated: "La echipa de suport",
	answered: "Răspuns",
	closed: "Închis",
}

export const SUPPORT_KINDS = ["question", "bug", "other"] as const
export type SupportKind = (typeof SUPPORT_KINDS)[number]
export const SUPPORT_KIND_LABEL_RO: Record<SupportKind, string> = {
	question: "Întrebare",
	bug: "Defect",
	other: "Altceva",
}

/** De ce a ajuns firul la om — intră în tichet și în statistici. */
export const ESCALATE_REASONS = [
	"no_ai", // asistentul nu e configurat/disponibil → direct la echipă
	"bug", // defect detectat (regula deterministă sau decizia modelului)
	"user_asked", // „vreau să vorbesc cu cineva”
	"cap", // al 3-lea răspuns fără rezolvare
	"llm_failed", // modelul a răspuns invalid de două ori
	"quota", // cota de AI epuizată (utilizator/primărie/instanță) sau cooldown
	"user_reply", // utilizatorul a revenit după răspunsul echipei
	"reopened", // mesaj nou pe un fir închis
	"staff", // un operator a preluat/redeschis firul
	"assistant", // asistentul a decis că e nevoie de om (nu știe răspunsul / cere intervenție)
] as const
export type EscalateReason = (typeof ESCALATE_REASONS)[number]

/**
 * Tranzițiile LEGALE (fail-closed), indiferent de actor; cine are voie să le declanșeze se judecă
 * în serviciu (utilizatorul: chat→escalated/closed, answered→escalated (mesaj nou), closed→escalated
 * (redeschidere), orice→closed (rezolvat); operatorul: escalated→answered/closed, answered→closed,
 * closed→escalated). „Aștept răspunsul la «s-a rezolvat?»” NU e stare: e derivată
 * (`status='chat' AND ai_replies >= 3`).
 */
export const SUPPORT_TRANSITIONS: Record<SupportStatus, readonly SupportStatus[]> = {
	chat: ["escalated", "closed"],
	escalated: ["answered", "closed"],
	answered: ["escalated", "closed"],
	closed: ["escalated"],
}
export function can_transition(from: SupportStatus, to: SupportStatus): boolean {
	return SUPPORT_TRANSITIONS[from].includes(to)
}
export function is_support_status(s: string): s is SupportStatus {
	return (SUPPORT_STATUSES as readonly string[]).includes(s)
}
export function is_support_kind(s: string): s is SupportKind {
	return (SUPPORT_KINDS as readonly string[]).includes(s)
}

export const MAX_SUPPORT_BODY = 2000
export const MAX_SUPPORT_SUBJECT = 80
export const MAX_SUPPORT_CONTEXT_BYTES = 8192
/** Un 5xx sau o excepție de client din ultimele 2 minute înaintea capturii = defect, nu întrebare. */
export const BUG_SIGNAL_WINDOW_MS = 2 * 60 * 1000

const S = (max: number) => Type.String({ maxLength: max })
export const SUPPORT_CONTEXT = Type.Object(
	{
		route: Type.Optional(S(200)), // hash + path, FĂRĂ query string
		map: Type.Optional(S(120)), // slug-ul hărții din workspace
		layer: Type.Optional(S(120)),
		feature: Type.Optional(S(64)),
		role: Type.Optional(S(32)),
		org_slug: Type.Optional(S(120)),
		features_on: Type.Optional(Type.Array(S(40), { maxItems: 16 })),
		features_off: Type.Optional(Type.Array(S(40), { maxItems: 16 })),
		app_version: Type.Optional(S(40)),
		ua: Type.Optional(S(300)),
		viewport: Type.Optional(S(20)),
		online: Type.Optional(Type.Boolean()),
		standalone: Type.Optional(Type.Boolean()),
		errors: Type.Optional(
			Type.Array(
				Type.Object(
					{ message: S(300), source: Type.Optional(S(200)), ts: Type.Optional(S(40)) },
					{ additionalProperties: false },
				),
				{ maxItems: 10 },
			),
		),
		failed_api: Type.Optional(
			Type.Array(
				Type.Object(
					{
						method: S(8),
						path: S(200), // FĂRĂ query string (căutările ar purta textul omului)
						status: Type.Integer({ minimum: 0, maximum: 999 }),
						ts: Type.Optional(S(40)),
					},
					{ additionalProperties: false },
				),
				{ maxItems: 10 },
			),
		),
		captured_at: Type.Optional(S(40)),
	},
	{ additionalProperties: false },
)
export type SupportContext = Static<typeof SUPPORT_CONTEXT>

/** Curățare defensivă: query string-urile cad (și dacă browserul le-ar trimite), spațiile se taie. */
const no_query = (s: string) => s.split("?")[0].trim()
export function normalize_context(ctx: SupportContext): SupportContext {
	return {
		...ctx,
		...(ctx.route !== undefined ? { route: no_query(ctx.route) } : {}),
		...(ctx.failed_api
			? { failed_api: ctx.failed_api.map((f) => ({ ...f, path: no_query(f.path) })) }
			: {}),
	}
}

/**
 * Regula deterministă de defect (D2/T5): un răspuns HTTP 5xx sau o excepție de client în ultimele
 * 2 minute înaintea capturii înseamnă `bug`, indiferent ce ar decide modelul. Intrările fără
 * timestamp parsabil contează (fail-closed spre „bug” — e mai bine un defect clasificat în plus decât
 * unul pierdut în „întrebări”).
 */
export function kind_from_context(
	ctx: SupportContext | null | undefined,
	now_ms = Date.now(),
): SupportKind {
	if (!ctx) {
		return "question"
	}
	const captured = ctx.captured_at ? Date.parse(ctx.captured_at) : Number.NaN
	const base = Number.isNaN(captured) ? now_ms : captured
	const recent = (ts?: string): boolean => {
		if (!ts) {
			return true
		}
		const t = Date.parse(ts)
		return Number.isNaN(t) || Math.abs(base - t) <= BUG_SIGNAL_WINDOW_MS
	}
	if ((ctx.failed_api ?? []).some((f) => f.status >= 500 && recent(f.ts))) {
		return "bug"
	}
	if ((ctx.errors ?? []).some((e) => recent(e.ts))) {
		return "bug"
	}
	return "question"
}

/** Subiectul firului: prima linie a primului mesaj, cel mult 80 de caractere. */
export function subject_of(body: string): string {
	const line =
		body
			.split(/\r?\n/)
			.map((l) => l.trim())
			.find((l) => l.length > 0) ?? ""
	const flat = line.replace(/\s+/g, " ")
	return flat.length > MAX_SUPPORT_SUBJECT
		? flat.slice(0, MAX_SUPPORT_SUBJECT - 1).trimEnd() + "…"
		: flat
}
